class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.setupEventListeners();
  }

  connectedCallback() {
    this.setupSearchLayoutSync();
    this.setupSearchSortBarLinks();
  }

  setupEventListeners() {
    let allSearchForms = [];
    this.allSearchInputs.forEach((input) => allSearchForms.push(input.form));
    this.input.addEventListener('focus', this.onInputFocus.bind(this));
    if (allSearchForms.length < 2) return;
    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }

  setupSearchLayoutSync() {
    if (this.controlsObserver || !document.querySelector('.catalog-layout--search')) return;

    this.syncSearchLayoutControls = this.syncSearchLayoutControls.bind(this);
    this.syncSearchLayoutControls();

    const observerTarget = this.closest('.section') || document.body;
    this.controlsObserver = new MutationObserver(() => {
      this.syncSearchLayoutControls();
    });
    this.controlsObserver.observe(observerTarget, { childList: true, subtree: true });
  }

  syncSearchLayoutControls() {
    const sidebarRoot = document.getElementById('cloud_search_filters_root');
    if (!sidebarRoot) {
      this.setupSearchSortBarLinks();
      return;
    }

    const sortLabel = this.normalizeText(sidebarRoot.dataset.sortLabel || 'sort by').replace(':', '');

    document.querySelectorAll('.cloud-search-page-controls').forEach((controls) => {
      if (!controls.closest('#cloud_search_filters_root') && !controls.hasAttribute('data-mobile-filters-controls')) {
        controls.style.display = 'none';
      }
    });

    sidebarRoot.querySelectorAll('select, .cloud-search-sort-select').forEach((element) => {
      const select = element.matches('select') ? element : element.querySelector('select');
      if (!select) return;
      if (!this.isSortSelect(select, sidebarRoot, sortLabel)) return;
      this.hideSortControl(select, sidebarRoot, sortLabel);
    });

    this.enhanceSidebarSelects(sidebarRoot, sortLabel);
    this.setupSearchSortBarLinks();
  }

  setupSearchSortBarLinks() {
    document.querySelectorAll('[data-search-sort-link][data-sort-value]').forEach((link) => {
      const sortValue = link.dataset.sortValue;
      if (!sortValue) return;

      const currentUrl = new URL(window.location.href);
      const params = new URLSearchParams(currentUrl.search);

      params.set('sort_by', sortValue);

      if (!params.has('options[prefix]')) {
        params.set('options[prefix]', 'last');
      }

      params.delete('page');

      link.href = `${currentUrl.pathname}?${params.toString()}${currentUrl.hash || ''}`;
    });
  }

  enhanceSidebarSelects(sidebarRoot, sortLabel) {
    const liveSelects = new Set();
    let selectIndex = 0;

    sidebarRoot.querySelectorAll('select').forEach((select) => {
      if (this.isSortSelect(select, sidebarRoot, sortLabel)) return;

      const meaningfulOptions = this.getMeaningfulOptions(select);
      if (meaningfulOptions.length === 0) return;

      if (!select.dataset.searchEnhancedId) {
        select.dataset.searchEnhancedId = `search-select-${selectIndex}-${Math.random().toString(36).slice(2, 8)}`;
      }
      selectIndex += 1;

      const selectId = select.dataset.searchEnhancedId;
      liveSelects.add(selectId);
      select.classList.add('template-search__native-select');

      let optionsContainer = sidebarRoot.querySelector(`[data-search-enhanced-options-for="${selectId}"]`);
      if (!optionsContainer) {
        optionsContainer = document.createElement('div');
        optionsContainer.className = 'template-search__enhanced-options';
        optionsContainer.dataset.searchEnhancedOptionsFor = selectId;
        select.insertAdjacentElement('afterend', optionsContainer);
      }

      this.renderEnhancedSelect(select, optionsContainer);
    });

    sidebarRoot.querySelectorAll('[data-search-enhanced-options-for]').forEach((container) => {
      if (!liveSelects.has(container.dataset.searchEnhancedOptionsFor)) {
        container.remove();
      }
    });
  }

  getMeaningfulOptions(select) {
    return Array.from(select.options).filter((option) => option.value && option.textContent.trim() !== '');
  }

  normalizeText(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  isKnownSortValue(value) {
    const normalizedValue = this.normalizeText(value);
    return [
      'relevance',
      'manual',
      'best-selling',
      'title-ascending',
      'title-descending',
      'price-ascending',
      'price-descending',
      'created-ascending',
      'created-descending',
    ].includes(normalizedValue);
  }

  hideSortControl(select, sidebarRoot, sortLabel) {
    let container = select.parentElement;
    let didHide = false;

    while (container && container !== sidebarRoot) {
      const text = this.normalizeText(container.textContent).replace(':', '');
      if (text.includes(sortLabel)) {
        container.style.display = 'none';
        didHide = true;
        break;
      }

      container = container.parentElement;
    }

    if (!didHide) {
      const fallbackContainer =
        select.closest('[class*="sort"], [id*="sort"]') || select.parentElement || select;
      fallbackContainer.style.display = 'none';
    }
  }

  isSortSelect(select, sidebarRoot, sortLabel) {
    const selectName = this.normalizeText(select.name);
    const selectId = this.normalizeText(select.id);
    const selectClass = this.normalizeText(select.className);
    const selectAriaLabel = this.normalizeText(select.getAttribute('aria-label'));

    if (
      selectName.includes('sort') ||
      selectId.includes('sort') ||
      selectClass.includes('sort') ||
      selectAriaLabel.includes('sort')
    ) {
      return true;
    }

    const options = this.getMeaningfulOptions(select);
    if (options.length && options.every((option) => this.isKnownSortValue(option.value))) {
      return true;
    }

    let container = select.parentElement;

    while (container && container !== sidebarRoot) {
      const text = this.normalizeText(container.textContent).replace(':', '');
      if (text.includes(sortLabel)) return true;
      container = container.parentElement;
    }

    return false;
  }

  renderEnhancedSelect(select, container) {
    const options = this.getMeaningfulOptions(select);
    const optionsSignature = options
      .map((option) => [option.value, option.textContent.trim(), option.disabled ? '1' : '0'].join('::'))
      .join('||');
    const resetOption = Array.from(select.options).find((option) => !option.value && !option.disabled);
    const isMultiple = select.multiple;

    if (container.dataset.searchEnhancedSignature !== optionsSignature) {
      container.textContent = '';
      container.dataset.searchEnhancedSignature = optionsSignature;
      container.dataset.searchEnhancedMode = isMultiple ? 'multiple' : 'single';

      options.forEach((option, optionIndex) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cloud-search-filter__name template-search__enhanced-option';
        button.dataset.searchOptionIndex = String(optionIndex);
        button.textContent = option.textContent.trim();
        container.appendChild(button);
      });
    }

    container.querySelectorAll('[data-search-option-index]').forEach((button) => {
      const option = options[Number(button.dataset.searchOptionIndex)];

      if (!option) return;

      const isActive = option.selected;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.disabled = option.disabled;

      if (!button.dataset.searchEnhancedBound) {
        button.addEventListener('click', () => {
          if (option.disabled) return;

          if (isMultiple) {
            option.selected = !option.selected;
          } else if (option.selected && resetOption) {
            select.value = resetOption.value;
          } else {
            select.value = option.value;
          }

          select.dispatchEvent(new Event('input', { bubbles: true }));
          select.dispatchEvent(new Event('change', { bubbles: true }));
          this.renderEnhancedSelect(select, container);
        });
        button.dataset.searchEnhancedBound = 'true';
      }
    });
  }

  disconnectedCallback() {
    this.controlsObserver?.disconnect();
  }
}

customElements.define('main-search', MainSearch);
